import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  ArrowRight,
  Camera,
  Loader2,
  Phone,
  ShieldCheck,
  Trash2,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import SchoolAutocomplete from '../components/SchoolAutocomplete';
import PhotoUploadDialog from '../components/PhotoUploadDialog';
import PhoneVerifyDialog from '../components/PhoneVerifyDialog';
import AppHeader from '../components/AppHeader';

const API = process.env.REACT_APP_BACKEND_URL;

const YEAR_OPTIONS = [
  ['just_starting', 'Just starting'],
  ['year_1', 'Year 1'],
  ['year_2', 'Year 2'],
  ['year_3', 'Year 3'],
  ['year_4_plus', 'Year 4+'],
  ['graduate', 'Graduate / Postgraduate'],
];

const SLEEP_OPTIONS = [
  ['early_riser', 'Early riser'],
  ['night_owl', 'Night owl'],
  ['flexible', 'Flexible'],
];
const CLEAN_OPTIONS = [
  ['very_tidy', 'Very tidy'],
  ['average', 'Average'],
  ['relaxed', 'Relaxed'],
];
const NOISE_OPTIONS = [
  ['very_quiet', 'Very quiet'],
  ['average', 'Average'],
  ['friends_over_often', 'I have friends over often'],
];
const DIET_OPTIONS = [
  ['no_preference', 'No preference'],
  ['vegetarian', 'Vegetarian'],
  ['vegan', 'Vegan'],
  ['halal', 'Halal'],
  ['kosher', 'Kosher'],
  ['other', 'Other'],
];
const SMOKE_OPTIONS = [
  ['non_smoker', 'Non-smoker'],
  ['outdoor_only', 'Outdoor only'],
  ['indoor_okay', 'Indoor okay'],
];
const DRINK_OPTIONS = [
  ['dont_drink', "Don't drink"],
  ['occasionally', 'Occasionally'],
  ['regularly', 'Regularly'],
];
const PET_OPTIONS = [
  ['no_pets', 'No pets'],
  ['has_pet', 'Has a pet'],
  ['want_pets', 'Want to live with pets'],
  ['allergic_to_pets', 'Allergic to pets'],
];
const PLACE_TYPE_OPTIONS = [
  ['private_room_shared_house', 'Private room in shared house'],
  ['basement_apartment', 'Basement apartment'],
  ['studio', 'Studio'],
  ['one_bedroom', '1 bedroom'],
  ['open_to_anything', 'Open to anything'],
];
const LIVING_OPTIONS = [
  ['just_me', 'Just me'],
  ['with_partner', 'With partner'],
  ['with_friends', 'With friends'],
  ['open_to_roommates', 'Open to roommates'],
];

const SectionCard = ({ title, subtitle, children, anchorId }) => (
  <section
    id={anchorId}
    className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-soft scroll-mt-24"
  >
    <h2 className="font-heading font-bold text-lg text-slate-900">{title}</h2>
    {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

const Field = ({ label, hint, children, htmlFor }) => (
  <div>
    <Label htmlFor={htmlFor} className="text-xs font-semibold text-slate-700">
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const ProfileEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api } = useAuth();
  const { bundle: ctxBundle, setBundle: setCtxBundle } = useProfile();
  const [bundle, setBundleLocal] = useState(ctxBundle);
  const setBundle = (b) => {
    setBundleLocal(b);
    setCtxBundle(b); // keep the global header + /home in sync after edits
  };
  const [loading, setLoading] = useState(!ctxBundle);
  const [saving, setSaving] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [countries, setCountries] = useState([]);
  const [languagesList, setLanguagesList] = useState([]);
  // Initialise with safe defaults for every array field so the JSX's
  // `form.languages.includes(...)` / `form.place_types_interested.includes(...)`
  // calls don't explode on the first render when we have a ctxBundle (which
  // sets `loading=false` immediately, before the useEffect below populates
  // form from the server response).
  const [form, setForm] = useState(() => ({
    preferred_name: ctxBundle?.user?.preferred_name || '',
    country_of_origin: ctxBundle?.user?.country_of_origin || '',
    languages: ctxBundle?.user?.languages || [],
    languages_other: ctxBundle?.user?.languages_other || '',
    program_of_study: ctxBundle?.user?.program_of_study || '',
    year_of_study: ctxBundle?.user?.year_of_study || '',
    expected_move_in_date: ctxBundle?.user?.expected_move_in_date || '',
    tagline: ctxBundle?.user?.tagline || '',
    long_bio: ctxBundle?.user?.long_bio || '',
    bio_prompt_studying: ctxBundle?.user?.bio_prompt_studying || '',
    bio_prompt_routine: ctxBundle?.user?.bio_prompt_routine || '',
    bio_prompt_freetime: ctxBundle?.user?.bio_prompt_freetime || '',
    bio_prompt_roommate: ctxBundle?.user?.bio_prompt_roommate || '',
    sleep_schedule: ctxBundle?.user?.sleep_schedule || '',
    cleanliness: ctxBundle?.user?.cleanliness || '',
    noise_level: ctxBundle?.user?.noise_level || '',
    dietary_preference: ctxBundle?.user?.dietary_preference || '',
    smoking: ctxBundle?.user?.smoking || '',
    drinking: ctxBundle?.user?.drinking || '',
    pets: ctxBundle?.user?.pets || '',
    open_to_roommates: !!ctxBundle?.user?.open_to_roommates,
    show_country_on_profile: ctxBundle?.user?.show_country_on_profile !== false,
    show_to_roommates: !!ctxBundle?.user?.show_to_roommates,
    allow_landlord_doc_requests: ctxBundle?.user?.allow_landlord_doc_requests !== false,
    school_name: ctxBundle?.user?.school_name || '',
    school_place_id: ctxBundle?.user?.school_place_id || '',
    school_lat: ctxBundle?.user?.school_lat || null,
    school_lng: ctxBundle?.user?.school_lng || null,
    monthly_budget_cad: ctxBundle?.user?.monthly_budget_cad || 1000,
    place_types_interested: ctxBundle?.user?.place_types_interested || [],
    living_situation: ctxBundle?.user?.living_situation || '',
  }));

  useEffect(() => {
    const load = async () => {
      // Prefer the context bundle (already fetched once globally) to avoid a
      // second /users/me round-trip when the user opens Edit Profile from the
      // header or /home. Only fetch when the context is empty (deep link).
      const [bRes, c, l] = await Promise.all([
        ctxBundle ? Promise.resolve({ data: ctxBundle }) : api.get('/users/me'),
        api.get('/lookups/countries'),
        api.get('/lookups/languages'),
      ]);
      const b = bRes;
      setBundle(b.data);
      setForm({
        preferred_name: b.data.user.preferred_name || '',
        country_of_origin: b.data.user.country_of_origin || '',
        languages: b.data.user.languages || [],
        languages_other: b.data.user.languages_other || '',
        program_of_study: b.data.user.program_of_study || '',
        year_of_study: b.data.user.year_of_study || '',
        expected_move_in_date: b.data.user.expected_move_in_date || '',
        tagline: b.data.user.tagline || '',
        long_bio: b.data.user.long_bio || '',
        bio_prompt_studying: b.data.user.bio_prompt_studying || '',
        bio_prompt_routine: b.data.user.bio_prompt_routine || '',
        bio_prompt_freetime: b.data.user.bio_prompt_freetime || '',
        bio_prompt_roommate: b.data.user.bio_prompt_roommate || '',
        sleep_schedule: b.data.user.sleep_schedule || '',
        cleanliness: b.data.user.cleanliness || '',
        noise_level: b.data.user.noise_level || '',
        dietary_preference: b.data.user.dietary_preference || '',
        smoking: b.data.user.smoking || '',
        drinking: b.data.user.drinking || '',
        pets: b.data.user.pets || '',
        open_to_roommates: !!b.data.user.open_to_roommates,
        show_country_on_profile: b.data.user.show_country_on_profile !== false,
        show_to_roommates: !!b.data.user.show_to_roommates,
        allow_landlord_doc_requests: b.data.user.allow_landlord_doc_requests !== false,
        school_name: b.data.user.school_name || '',
        school_place_id: b.data.user.school_place_id || '',
        school_lat: b.data.user.school_lat || null,
        school_lng: b.data.user.school_lng || null,
        monthly_budget_cad: b.data.user.monthly_budget_cad || 1000,
        place_types_interested: b.data.user.place_types_interested || [],
        living_situation: b.data.user.living_situation || '',
      });
      setCountries(c.data.countries);
      setLanguagesList(l.data.languages);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  // Deep-link support: /profile/edit#about → scroll to the About section and
  // highlight its first input. Used by Trust Score breakdown rows and by the
  // /profile/me "Add about me" CTA.
  useEffect(() => {
    if (loading) return;
    const hash = (location.hash || '').replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      // Give the page a tick to paint before scrolling.
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, [location.hash, loading]);

  const toggleLanguage = (lang) => {
    setForm((f) => {
      const has = f.languages.includes(lang);
      return { ...f, languages: has ? f.languages.filter((l) => l !== lang) : [...f.languages, lang] };
    });
  };
  const togglePlaceType = (pt) => {
    setForm((f) => {
      const has = f.place_types_interested.includes(pt);
      return {
        ...f,
        place_types_interested: has
          ? f.place_types_interested.filter((p) => p !== pt)
          : [...f.place_types_interested, pt],
      };
    });
  };

  const photoUrl = useMemo(() => {
    if (!bundle?.profile_photo_token) return null;
    return `${API}/api/files/${bundle.profile_photo_token}`;
  }, [bundle?.profile_photo_token]);

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty strings to "" (don't send null on optional unchecked).
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') payload[k] = null;
      });
      // pydantic excludes None on backend, so empty strings are dropped — that's fine.
      const res = await api.patch('/users/me/profile', payload);
      setBundle(res.data);
      toast.success('Profile saved.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save. Try again?');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  const u = bundle.user;
  const initial = (u.first_name || u.email || '?').charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="profile-edit-page">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-extrabold text-slate-900">Edit profile</h1>
          <Button
            onClick={save}
            disabled={saving}
            className="btn-primary h-10 px-4 text-sm hidden sm:inline-flex"
            data-testid="profile-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}
          </Button>
        </div>
        {/* PHOTO + IDENTITY */}
        <SectionCard title="Personal" subtitle="Your basics. We only show what landlords need to see." anchorId="personal">
          <div className="flex items-center gap-4" data-testid="photo-row">
            <div className="h-20 w-20 rounded-2xl overflow-hidden bg-amber-500 flex items-center justify-center shadow-soft border-2 border-white">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-heading font-bold text-2xl">{initial}</span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">Profile photo</p>
              <p className="text-xs text-slate-500">JPG, PNG, or WebP — max 5 MB</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-300"
                  onClick={() => setShowPhoto(true)}
                  data-testid="photo-edit-btn"
                >
                  <Camera className="h-4 w-4 mr-1.5" />
                  {photoUrl ? 'Change' : 'Upload'}
                </Button>
                {photoUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={async () => {
                      const res = await api.delete('/users/me/photo');
                      setBundle(res.data);
                      toast.success('Photo removed.');
                    }}
                    data-testid="photo-remove-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Field label="First name (read-only — set during signup)">
            <Input value={u.first_name} disabled className="h-12 bg-slate-50" />
          </Field>

          <Field label="Preferred name (optional)" htmlFor="preferred-name">
            <Input
              id="preferred-name"
              value={form.preferred_name}
              onChange={(e) => set('preferred_name')(e.target.value)}
              maxLength={80}
              placeholder="What you'd like to be called"
              className="h-12 bg-white"
              data-testid="preferred-name-input"
            />
          </Field>

          <Field label="Phone number">
            <div className="flex items-center gap-3">
              {u.phone ? (
                <>
                  <span className="text-sm text-slate-700 font-medium">
                    {u.phone_country_code} {u.phone}
                  </span>
                  {u.phone_verified ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <ShieldCheck className="h-3.5 w-3.5" /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                      Not verified
                    </span>
                  )}
                </>
              ) : (
                <span className="text-sm text-slate-500">Not added</span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPhone(true)}
                className="ml-auto border-slate-300"
                data-testid="phone-edit-btn"
              >
                <Phone className="h-4 w-4 mr-1.5" />
                {u.phone_verified ? 'Change' : u.phone ? 'Verify' : 'Add'}
              </Button>
            </div>
          </Field>

          <Field
            label="Country of origin (optional)"
            hint="Helps roommates find people from similar backgrounds."
          >
            <Select value={form.country_of_origin || ''} onValueChange={set('country_of_origin')}>
              <SelectTrigger className="h-12 bg-white" data-testid="country-select">
                <SelectValue placeholder="Select your country" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {countries.map((c) => (
                  <SelectItem key={c.code} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Languages spoken (optional)" hint="Pick all that apply.">
            <div className="flex flex-wrap gap-2" data-testid="languages-row">
              {languagesList.map((lang) => {
                const on = form.languages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLanguage(lang)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      on
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'
                    }`}
                    data-testid={`lang-chip-${lang}`}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
            {form.languages.includes('Other') && (
              <Input
                value={form.languages_other}
                onChange={(e) => set('languages_other')(e.target.value)}
                maxLength={80}
                placeholder="Which other language?"
                className="h-11 mt-2 bg-white"
                data-testid="languages-other-input"
              />
            )}
          </Field>
        </SectionCard>

        {/* ACADEMIC */}
        <SectionCard title="Academic" anchorId="academic">
          <Field label="School">
            <SchoolAutocomplete
              value={form.school_name}
              onSelect={(p) =>
                setForm((f) => ({
                  ...f,
                  school_name: p.name,
                  school_place_id: p.place_id,
                  school_lat: p.lat,
                  school_lng: p.lng,
                }))
              }
              className="[&_input]:h-12 [&_input]:bg-white"
            />
          </Field>

          <Field label="Program of study (optional)" htmlFor="program">
            <Input
              id="program"
              value={form.program_of_study}
              onChange={(e) => set('program_of_study')(e.target.value)}
              maxLength={120}
              placeholder="e.g. Computer Science"
              className="h-12 bg-white"
              data-testid="program-input"
            />
          </Field>

          <Field label="Year of study (optional)">
            <Select value={form.year_of_study || ''} onValueChange={set('year_of_study')}>
              <SelectTrigger className="h-12 bg-white" data-testid="year-select">
                <SelectValue placeholder="Pick your year" />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Expected move-in date">
            <Input
              type="date"
              value={form.expected_move_in_date || ''}
              onChange={(e) => set('expected_move_in_date')(e.target.value)}
              className="h-12 bg-white"
              data-testid="movein-input"
            />
          </Field>
        </SectionCard>

        {/* ABOUT ME */}
        <SectionCard title="About me" subtitle="What landlords and potential roommates see." anchorId="about">
          <Field
            label="Tagline"
            hint="One line that captures who you are. Example: First-year CS student from Mumbai, vegetarian, early riser."
            htmlFor="tagline"
          >
            <Input
              id="tagline"
              value={form.tagline}
              onChange={(e) => set('tagline')(e.target.value)}
              maxLength={80}
              placeholder="One short line about you"
              className="h-12 bg-white"
              data-testid="tagline-input"
            />
            <p className="mt-1 text-xs text-slate-400 text-right">{form.tagline.length}/80</p>
          </Field>

          <Field
            label="Long bio (optional)"
            hint="A few sentences for landlords and potential roommates. What are you studying? What's your day like? What kind of person are you to live with?"
          >
            <Textarea
              value={form.long_bio}
              onChange={(e) => set('long_bio')(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Tell us a bit more"
              className="bg-white"
              data-testid="long-bio-input"
            />
            <p className="mt-1 text-xs text-slate-400 text-right">{form.long_bio.length}/500</p>
          </Field>

          <Field label="Or fill these prompts (optional)">
            <div className="space-y-3">
              {[
                ['bio_prompt_studying', 'What are you studying?'],
                ['bio_prompt_routine', "What's your daily routine like?"],
                ['bio_prompt_freetime', 'What do you enjoy in your free time?'],
                ['bio_prompt_roommate', 'What kind of roommate are you?'],
              ].map(([k, q]) => (
                <div key={k}>
                  <p className="text-xs text-slate-600 mb-1">{q}</p>
                  <Input
                    value={form[k]}
                    onChange={(e) => set(k)(e.target.value)}
                    maxLength={100}
                    placeholder="Up to 100 characters"
                    className="h-11 bg-white"
                    data-testid={`prompt-${k}`}
                  />
                </div>
              ))}
            </div>
          </Field>
        </SectionCard>

        {/* LIFESTYLE */}
        <SectionCard title="Lifestyle" anchorId="lifestyle"
          subtitle="Used to match you with compatible roommates. Hidden from landlords."
        >
          {[
            ['sleep_schedule', 'Sleep schedule', SLEEP_OPTIONS],
            ['cleanliness', 'Cleanliness', CLEAN_OPTIONS],
            ['noise_level', 'Noise level', NOISE_OPTIONS],
            ['dietary_preference', 'Dietary preference', DIET_OPTIONS],
            ['smoking', 'Smoking', SMOKE_OPTIONS],
            ['drinking', 'Drinking', DRINK_OPTIONS],
            ['pets', 'Pets', PET_OPTIONS],
          ].map(([k, label, opts]) => (
            <Field key={k} label={label}>
              <Select value={form[k] || ''} onValueChange={set(k)}>
                <SelectTrigger className="h-12 bg-white" data-testid={`lifestyle-${k}`}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {opts.map(([v, l]) => (
                    <SelectItem key={v} value={v}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ))}

          <div className="flex items-center justify-between py-2">
            <div className="pr-4">
              <p className="text-sm font-semibold text-slate-900">Open to roommates</p>
              <p className="text-xs text-slate-500">Show me to people looking for roommates.</p>
            </div>
            <Switch
              checked={form.open_to_roommates}
              onCheckedChange={set('open_to_roommates')}
              data-testid="toggle-open-to-roommates"
            />
          </div>
        </SectionCard>

        {/* PREFERENCES */}
        <SectionCard title="Search preferences" anchorId="preferences">
          <Field label={`Monthly budget (CAD): $${form.monthly_budget_cad}`}>
            <Slider
              min={500}
              max={2500}
              step={50}
              value={[form.monthly_budget_cad]}
              onValueChange={(v) => set('monthly_budget_cad')(v[0])}
              data-testid="budget-slider"
            />
          </Field>

          <Field label="Place types I'm interested in">
            <div className="flex flex-wrap gap-2">
              {PLACE_TYPE_OPTIONS.map(([v, l]) => {
                const on = form.place_types_interested.includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => togglePlaceType(v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      on
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'
                    }`}
                    data-testid={`place-chip-${v}`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Living situation">
            <Select value={form.living_situation || ''} onValueChange={set('living_situation')}>
              <SelectTrigger className="h-12 bg-white" data-testid="living-select">
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                {LIVING_OPTIONS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </SectionCard>

        {/* PRIVACY */}
        <SectionCard title="Privacy" anchorId="privacy">
          {[
            ['show_country_on_profile', 'Show my country of origin on my profile', true],
            ['show_to_roommates', 'Show my profile to potential roommates', false],
            ['allow_landlord_doc_requests', 'Allow landlords to request my documents', true],
          ].map(([k, label]) => (
            <div key={k} className="flex items-center justify-between py-2">
              <p className="text-sm text-slate-800 pr-4">{label}</p>
              <Switch
                checked={!!form[k]}
                onCheckedChange={set(k)}
                data-testid={`privacy-${k}`}
              />
            </div>
          ))}
        </SectionCard>

        {/* Documents shortcut */}
        <Link
          to="/profile/documents"
          className="block bg-white border border-slate-200 rounded-2xl p-5 shadow-soft hover:shadow-hover transition-shadow"
          data-testid="documents-shortcut"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-heading font-bold text-base text-slate-900">My documents</p>
              <p className="text-sm text-slate-600 mt-0.5">
                Acceptance letter, ID, study permit, references.
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400" />
          </div>
        </Link>

        {/* References anchor target — the Trust Score breakdown links here.
            The full references editor ships in Phase 2; for now we just pin a
            clear next-step card so deep links don't look broken. */}
        <section
          id="references"
          className="bg-amber-50 border border-amber-200/60 rounded-2xl p-5 shadow-soft scroll-mt-24"
          data-testid="references-section"
        >
          <h2 className="font-heading font-bold text-lg text-slate-900">References</h2>
          <p className="text-sm text-slate-700 mt-1 leading-relaxed">
            Add 1–2 references (a past landlord, a roommate, or your school housing office)
            to boost your Trust Score and land replies faster. We'll email them a short one-click
            confirmation — they never see your private details.
          </p>
          <p className="mt-3 text-xs text-slate-500">
            Reference collection goes live in Phase 2. In the meantime, upload an acceptance
            letter and verify your phone to unlock messaging.
          </p>
        </section>
      </main>

      {/* Sticky save bar on mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 sm:hidden z-20" data-testid="sticky-save-bar">
        <Button
          onClick={save}
          disabled={saving}
          className="w-full btn-primary py-6 text-base"
          data-testid="profile-save-btn-mobile"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save profile'}
        </Button>
      </div>

      <PhotoUploadDialog
        open={showPhoto}
        onOpenChange={setShowPhoto}
        onUpdated={(b) => setBundle(b)}
      />
      <PhoneVerifyDialog
        open={showPhone}
        onOpenChange={setShowPhone}
        initialPhone={u.phone || ''}
        initialCode={u.phone_country_code || '+1'}
        onVerified={(b) => setBundle(b)}
      />
    </div>
  );
};

export default ProfileEdit;
